import { memo, useContext, useRef, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { CallTopicContextProvider } from '../../contexts/CallTopicContext';
import MediaTimelineOverlay from '../components/MediaTimelineComponents/MediaTimelineOverlay';
import MediaProgressBar from '../components/MediaTimelineComponents/MediaProgressBar';
import MediaSpeakerTimeline from '../components/MediaTimelineComponents/MediaSpeakerTimeline';
import ToggleMediaTimelineButton from '../components/MediaTimelineComponents/ToggleMediaTimelineButton';
import CallTopicsList from '../components/MediaTimelineComponents/CallTopicsList';
import CallTopicsTimeline from '../components/MediaTimelineComponents/CallTopicsTimeline';
import { ADVANCED } from '../../types/MediaTimelineTypes';
import { MediaPlaybackControlsContext } from '../context/MediaPlaybackControlsContext';
import { Flex } from 'foundations-components/components/Flex';
import { Box } from 'foundations-components/transitional/components/Box';
import { View } from 'foundations-components/components/View';
import { LEFT_COL_WIDTH, RIGHT_COL_WIDTH } from '../constants/MediaTimelinePositioning';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledAudioReference = styled.div.withConfig({
  displayName: "MediaTimeline__StyledAudioReference"
})(["position:absolute;left:", ";right:", ";"], LEFT_COL_WIDTH, RIGHT_COL_WIDTH);
const StyledTimelineContainer = styled.div.withConfig({
  displayName: "MediaTimeline__StyledTimelineContainer"
})(["user-select:none;"]);

/**
 * @deprecated
 * @todo Remove this after we merge the changes from https://git.hubteam.com/HubSpot/transcript-sidebar/pull/2357
 */

const MediaTimeline = /*#__PURE__*/memo(props => {
  const {
    setPlayerHeadLocation,
    preciseDuration
  } = useContext(MediaPlaybackControlsContext);
  const {
    variant,
    showCallTopics,
    transcriptUtterances,
    detailedSpeakers,
    onExpandCallback,
    onCollapseCallback,
    onDragCallback,
    onCallTopicsClick
  } = props;
  const [isMediaTimelineExpanded, setIsMediaTimelineExpanded] = useState(true);
  const timelineRef = useRef(null);
  const handleToggleMediaTimeline = useCallback(() => {
    if (isMediaTimelineExpanded && onCollapseCallback) {
      onCollapseCallback();
    } else if (!isMediaTimelineExpanded && onExpandCallback) {
      onExpandCallback();
    }
    setIsMediaTimelineExpanded(!isMediaTimelineExpanded);
  }, [isMediaTimelineExpanded, onCollapseCallback, onExpandCallback]);
  const footerControls = useMemo(() => {
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      alignItems: "center",
      children: [showCallTopics && /*#__PURE__*/_jsx(AccordionTransition, {
        open: isMediaTimelineExpanded,
        children: /*#__PURE__*/_jsx(CallTopicsList, {})
      }), transcriptUtterances && transcriptUtterances.length !== 0 ? /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(ToggleMediaTimelineButton, {
          isMediaTimelineExpanded: isMediaTimelineExpanded,
          handleToggleMediaTimeline: handleToggleMediaTimeline
        })
      }) : null]
    });
  }, [handleToggleMediaTimeline, isMediaTimelineExpanded, showCallTopics, transcriptUtterances]);
  const accordionContainer = useMemo(() => {
    return /*#__PURE__*/_jsx(Box, {
      "data-test-id": "mediaTimeline-accordionContainer",
      marginBlockStart: transcriptUtterances && transcriptUtterances.length !== 0 ? '600' : '0',
      children: /*#__PURE__*/_jsx(AccordionTransition, {
        open: isMediaTimelineExpanded,
        children: /*#__PURE__*/_jsx(StyledTimelineContainer, {
          children: /*#__PURE__*/_jsxs(TFlex, {
            direction: "column",
            justify: "center",
            align: "stretch",
            children: [/*#__PURE__*/_jsx(Box, {
              children: /*#__PURE__*/_jsx(MediaSpeakerTimeline, {
                transcriptUtterances: transcriptUtterances || [],
                speakers: detailedSpeakers || []
              })
            }), showCallTopics && /*#__PURE__*/_jsx(CallTopicsTimeline, {})]
          })
        })
      })
    });
  }, [detailedSpeakers, isMediaTimelineExpanded, showCallTopics, transcriptUtterances]);
  return /*#__PURE__*/_jsx(CallTopicContextProvider, {
    transcriptUtterances: transcriptUtterances || [],
    setCurrentTime: setPlayerHeadLocation,
    showCallTopics: showCallTopics || false,
    durationInSeconds: preciseDuration,
    onCallTopicsClick: onCallTopicsClick,
    children: /*#__PURE__*/_jsxs(Flex, {
      "data-test-id": "mediaTimeline",
      alignItems: "center",
      flexDirection: "column",
      position: "relative",
      blockSize: "100%",
      children: [/*#__PURE__*/_jsx(StyledAudioReference, {
        ref: timelineRef
      }), /*#__PURE__*/_jsxs(View, {
        position: "relative",
        inlineSize: "100%",
        zIndex: 1,
        blockSize: "100%",
        children: [/*#__PURE__*/_jsx(Box, {
          blockSize: "100%",
          minBlockSize: "18px",
          children: /*#__PURE__*/_jsx(MediaProgressBar, {
            variant: variant,
            isMediaTimelineExpanded: isMediaTimelineExpanded,
            transcriptUtterances: transcriptUtterances
          })
        }), variant === ADVANCED && accordionContainer, /*#__PURE__*/_jsx(MediaTimelineOverlay, {
          timelineRef: timelineRef,
          onDragCallback: onDragCallback
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        alignItems: "center",
        children: variant === ADVANCED && footerControls
      })]
    })
  });
});
export { MediaTimeline };